import { useEffect, useMemo, useState } from 'react';
import { useRequest } from 'ahooks';
import { getDepartmentList, StoreInfo } from 'services/api/dashboard';
import { Button, message, Radio, Tabs } from 'antd';
import { useTranslate } from '@tolgee/react';
import { DownloadOutlined, LoadingOutlined } from '@ant-design/icons';
import { getStoreCount } from 'components/StoreScopes';
import Empty from 'components/Empty';
import styled from '@emotion/styled';
import Visible from './Visible';
import ShopeePerformance from './ShopeePerformance';
import CsatDetail from './CsatDetail';
import LazadaPerformance from './LazadaPerformance';
import TiktokPerformance from './TiktokPerformance';
import { eventBus } from 'services/eventBus';
import ConversationModal from 'views/Setting/Remind/components/ConversationModal';
import useModal from 'hooks/useModal';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import ExportModal from './ExportModal';

function PlatformData() {
  const { t } = useTranslate();

  const exportable = useGetPermission()('导出平台数据');

  const [platform, setPlatform] = useState<General.PlatformUpperCase>();

  const [type, setType] = useState('performance');

  useEffect(() => {
    if (platform === 'LAZADA' && type !== 'performance') {
      setType('performance');
    }
  }, [platform, type]);

  const { loading, data } = useRequest(
    () =>
      getDepartmentList('store_performance').then((res) => {
        const platform = (['SHOPEE', 'LAZADA', 'TIKTOK', 'TIKTOK_US'] as const).find((platform) => {
          return res.departments.some(({ platformStoreScopes }) =>
            getStoreCount(platformStoreScopes.filter((_) => _.platform === platform)),
          );
        });
        if (platform) {
          setPlatform(platform);
        }
        return res.departments;
      }),
    {
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  const {
    shopeeDepartments,
    lazadaDepartments,
    tiktokDepartments,
    tiktokUsDepartments,
    platforms,
  } = useMemo(() => {
    const shopeeDepartments =
      data?.filter(({ platformStoreScopes }) =>
        getStoreCount(platformStoreScopes.filter(({ platform }) => platform === 'SHOPEE')),
      ) || [];
    const lazadaDepartments =
      data?.filter(({ platformStoreScopes }) =>
        getStoreCount(platformStoreScopes.filter(({ platform }) => platform === 'LAZADA')),
      ) || [];
    const tiktokDepartments =
      data?.filter(({ platformStoreScopes }) =>
        getStoreCount(platformStoreScopes.filter(({ platform }) => platform === 'TIKTOK')),
      ) || [];
    const tiktokUsDepartments =
      data?.filter(({ platformStoreScopes }) =>
        getStoreCount(platformStoreScopes.filter(({ platform }) => platform === 'TIKTOK_US')),
      ) || [];
    const platforms: General.PlatformUpperCase[] = [];
    if (shopeeDepartments.length) {
      platforms.push('SHOPEE');
    }
    if (lazadaDepartments.length) {
      platforms.push('LAZADA');
    }
    if (tiktokDepartments.length) {
      platforms.push('TIKTOK');
    }
    if (tiktokUsDepartments.length) {
      platforms.push('TIKTOK_US');
    }
    return {
      shopeeDepartments,
      lazadaDepartments,
      tiktokDepartments,
      tiktokUsDepartments,
      platforms,
    };
  }, [data]);

  const [initStoreInfo, setInitStoreInfo] = useState<StoreInfo>();

  useEffect(() => {
    const handler = (storeInfo: StoreInfo) => {
      const { platform } = storeInfo;
      setPlatform(platform);
      setType('csat');
      setInitStoreInfo(storeInfo);
    };

    eventBus.on('CHECK_STORE_PLATFORM_CSAT_DETAIL', handler);

    return () => eventBus.off('CHECK_STORE_PLATFORM_CSAT_DETAIL', handler);
  }, []);

  const { visible, show, hide, meta } = useModal<Dashboard.PlatformData.CsatData>();

  const conversationModal = meta && (
    <ConversationModal
      visible={visible}
      onCancel={() => hide()}
      record={{
        buyerNick: meta.buyerNick,
        conversationId: meta.innerConversationId.toString(),
      }}
      searchMsgId={meta.innerMessageId.toString()}
      searchMsgSendTime={meta.scoreTimeStamp}
      searchBlock="end"
    />
  );

  const exportModalState = useModal();

  if (!data) {
    return loading ? <LoadingOutlined /> : <></>;
  }

  if (!platform) {
    return <Empty />;
  }

  return (
    <Container>
      <Tabs
        activeKey={platform}
        onChange={setPlatform as any}
        tabBarExtraContent={
          exportable && (
            <Button className="ml-4" icon={<DownloadOutlined />} onClick={exportModalState.show} />
          )
        }
      >
        {shopeeDepartments.length > 0 && <Tabs.TabPane key="SHOPEE" tab="Shopee" />}
        {lazadaDepartments.length > 0 && <Tabs.TabPane key="LAZADA" tab="Lazada" />}
        {tiktokDepartments.length > 0 && <Tabs.TabPane key="TIKTOK" tab="TikTok" />}
        {tiktokUsDepartments.length > 0 && <Tabs.TabPane key="TIKTOK_US" tab="TikTok US" />}
      </Tabs>
      <ExportModal
        visible={exportModalState.visible}
        onCancel={exportModalState.hide}
        departments={data}
        platforms={platforms}
      />
      {platform !== 'LAZADA' && (
        <div>
          <Radio.Group value={type} onChange={({ target: { value } }) => setType(value)}>
            <Radio.Button value="performance">{t('店铺表现')}</Radio.Button>
            <Radio.Button value="csat">{t('CSAT明细')}</Radio.Button>
          </Radio.Group>
        </div>
      )}
      <section>
        {shopeeDepartments.length > 0 && [
          <Visible visible={platform === 'SHOPEE' && type === 'performance'}>
            <ShopeePerformance departments={shopeeDepartments} />
          </Visible>,
          <Visible visible={platform === 'SHOPEE' && type === 'csat'}>
            <CsatDetail
              platform="SHOPEE"
              departments={shopeeDepartments}
              initStoreInfo={initStoreInfo?.platform === 'SHOPEE' ? initStoreInfo : undefined}
              onCheckConversation={show}
            />
          </Visible>,
        ]}
        {lazadaDepartments.length > 0 && [
          <Visible visible={platform === 'LAZADA' && type === 'performance'}>
            <LazadaPerformance departments={lazadaDepartments} />
          </Visible>,
        ]}
        {tiktokDepartments.length > 0 && [
          <Visible visible={platform === 'TIKTOK' && type === 'performance'}>
            <TiktokPerformance platform="TIKTOK" departments={tiktokDepartments} />
          </Visible>,
          <Visible visible={platform === 'TIKTOK' && type === 'csat'}>
            <CsatDetail
              platform="TIKTOK"
              departments={tiktokDepartments}
              initStoreInfo={initStoreInfo?.platform === 'TIKTOK' ? initStoreInfo : undefined}
              onCheckConversation={show}
            />
          </Visible>,
        ]}
        {tiktokUsDepartments.length > 0 && [
          <Visible visible={platform === 'TIKTOK_US' && type === 'performance'}>
            <TiktokPerformance platform="TIKTOK_US" departments={tiktokUsDepartments} />
          </Visible>,
          <Visible visible={platform === 'TIKTOK_US' && type === 'csat'}>
            <CsatDetail
              platform="TIKTOK_US"
              departments={tiktokUsDepartments}
              initStoreInfo={initStoreInfo?.platform === 'TIKTOK_US' ? initStoreInfo : undefined}
              onCheckConversation={show}
            />
          </Visible>,
        ]}
      </section>
      {conversationModal}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  > .ant-tabs {
    position: sticky;
    top: 0;
    margin-top: 16px;
    background: #fff;
    z-index: 2;
    .ant-tabs-nav {
      padding: 0 16px;
      margin-bottom: 0;
    }
  }
  > div {
    flex: 0 0 auto;
  }
  > .ant-tabs + div {
    padding: 8px 16px;
    background-color: #fff;
  }
  > section {
    flex: 1 0 0;
    > div {
      height: 100%;
    }
  }
`;

export default PlatformData;
